// Single localStorage key + debounced writer for both editor history stacks so they can't desync on reload.

import type { Page } from '@/types/page';
import {
  cloneStyles,
  emptySiteStylesHistory,
  type PersistedSiteStylesHistory,
  SITE_HISTORY_LIMIT,
  type SiteStyles,
} from './site-styles-shared';

export type HistoryKind = 'page' | 'site';

export interface PersistedPageEditorHistory {
  historyPast: Page[];
  historyFuture: Page[];
  historyPastTargets: (string | null)[];
  historyFutureTargets: (string | null)[];
  historyOrder: HistoryKind[];
  historyOrderFuture: HistoryKind[];
}

export interface CombinedEditorHistoryBlob {
  page: PersistedPageEditorHistory;
  site: PersistedSiteStylesHistory;
}

const STORAGE_KEY_PREFIX = 'll-editor-history-v1';
const PERSIST_DEBOUNCE_MS = 300;

const getStorageKey = (accountId: string): string => `${STORAGE_KEY_PREFIX}:${accountId}`;

const getStorage = (): Storage | null => {
  try {
    return globalThis.window?.localStorage ?? null;
  } catch {
    return null;
  }
};

let isApplyingPersisted = false;

export const withApplyingPersistedEditorHistory = (fn: () => void): void => {
  isApplyingPersisted = true;
  try {
    fn();
  } finally {
    isApplyingPersisted = false;
  }
};

const emptyPageHistory = (): PersistedPageEditorHistory => ({
  historyPast: [],
  historyFuture: [],
  historyPastTargets: [],
  historyFutureTargets: [],
  historyOrder: [],
  historyOrderFuture: [],
});

export const emptyCombinedEditorHistory = (): CombinedEditorHistoryBlob => ({
  page: emptyPageHistory(),
  site: emptySiteStylesHistory(),
});

let pageProvider: (() => PersistedPageEditorHistory) | null = null;
let siteProvider: (() => PersistedSiteStylesHistory) | null = null;
let accountIdProvider: (() => string | null) | null = null;

export const registerPageHistoryProvider = (fn: () => PersistedPageEditorHistory): void => {
  pageProvider = fn;
};

export const registerSiteHistoryProvider = (fn: () => PersistedSiteStylesHistory): void => {
  siteProvider = fn;
};

export const registerHistoryAccountIdProvider = (fn: () => string | null): void => {
  accountIdProvider = fn;
};

let writeTimer: ReturnType<typeof setTimeout> | null = null;

export const scheduleEditorHistoryWrite = (): void => {
  if (isApplyingPersisted) return;
  if (!accountIdProvider || !pageProvider || !siteProvider) return;

  if (writeTimer) clearTimeout(writeTimer);
  writeTimer = setTimeout(() => {
    writeTimer = null;
    const accountId = accountIdProvider?.();
    if (!accountId) return;
    const storage = getStorage();
    if (!storage) return;

    const blob: CombinedEditorHistoryBlob = {
      page: pageProvider?.() ?? emptyPageHistory(),
      site: siteProvider?.() ?? emptySiteStylesHistory(),
    };

    try {
      storage.setItem(getStorageKey(accountId), JSON.stringify(blob));
    } catch {
      storage.removeItem(getStorageKey(accountId));
    }
  }, PERSIST_DEBOUNCE_MS);
};

// Persist the combined history blob immediately, cancelling any pending debounce.
// Call before a programmatic reload so in-memory undo/redo state survives it.
export const flushEditorHistoryWrite = (): void => {
  if (writeTimer) {
    clearTimeout(writeTimer);
    writeTimer = null;
  }
  if (!accountIdProvider || !pageProvider || !siteProvider) return;
  const accountId = accountIdProvider();
  if (!accountId) return;
  const storage = getStorage();
  if (!storage) return;

  const blob: CombinedEditorHistoryBlob = {
    page: pageProvider() ?? emptyPageHistory(),
    site: siteProvider() ?? emptySiteStylesHistory(),
  };

  try {
    storage.setItem(getStorageKey(accountId), JSON.stringify(blob));
  } catch {
    storage.removeItem(getStorageKey(accountId));
  }
};

const isHistoryKind = (value: unknown): value is HistoryKind =>
  value === 'page' || value === 'site';

const normalizeTargetArray = (value: unknown): (string | null)[] =>
  Array.isArray(value) ? value.map((t) => (typeof t === 'string' ? t : null)) : [];

const HISTORY_LIMIT = 50;

// Trim `order` so its 'site' tag count matches `siteCount`; aligns page snapshots to remaining 'page' tags.
const reconcilePageOrder = (
  order: HistoryKind[],
  pageSnapshots: unknown[],
  pageTargets: (string | null)[],
  siteCount: number,
): { order: HistoryKind[]; pageSnapshots: Page[]; pageTargets: (string | null)[] } => {
  let pageIndex = 0;
  const nextOrder: HistoryKind[] = [];
  const nextPages: Page[] = [];
  const nextTargets: (string | null)[] = [];
  let siteSeen = 0;

  for (const kind of order) {
    if (kind === 'site') {
      if (siteSeen >= siteCount) continue;
      siteSeen += 1;
      nextOrder.push('site');
      continue;
    }
    const snap = pageSnapshots[pageIndex];
    const target = pageTargets[pageIndex] ?? null;
    pageIndex += 1;
    if (!isPage(snap)) continue;
    nextOrder.push('page');
    nextPages.push(snap);
    nextTargets.push(target);
  }

  return {
    order: nextOrder.slice(-HISTORY_LIMIT),
    pageSnapshots: nextPages.slice(-HISTORY_LIMIT),
    pageTargets: nextTargets.slice(-HISTORY_LIMIT),
  };
};

const isPage = (value: unknown): value is Page =>
  Boolean(value) && typeof value === 'object' && Boolean((value as Page).id);

const normalizePageSide = (
  raw: Partial<PersistedPageEditorHistory> | undefined,
  siteCount: number,
): {
  order: HistoryKind[];
  pageSnapshots: Page[];
  pageTargets: (string | null)[];
} => {
  const orderRaw = Array.isArray(raw?.historyOrder) ? raw.historyOrder : [];
  const order = orderRaw.filter(isHistoryKind).slice(-HISTORY_LIMIT);
  const snapshots = Array.isArray(raw?.historyPast) ? (raw.historyPast as unknown[]) : [];
  const targets = normalizeTargetArray(raw?.historyPastTargets);
  return reconcilePageOrder(order, snapshots, targets, siteCount);
};

const normalizePageSideFuture = (
  raw: Partial<PersistedPageEditorHistory> | undefined,
  siteCount: number,
): {
  order: HistoryKind[];
  pageSnapshots: Page[];
  pageTargets: (string | null)[];
} => {
  const orderRaw = Array.isArray(raw?.historyOrderFuture) ? raw.historyOrderFuture : [];
  const order = orderRaw.filter(isHistoryKind).slice(-HISTORY_LIMIT);
  const snapshots = Array.isArray(raw?.historyFuture) ? (raw.historyFuture as unknown[]) : [];
  const targets = normalizeTargetArray(raw?.historyFutureTargets);
  return reconcilePageOrder(order, snapshots, targets, siteCount);
};

const normalizeSiteSide = (
  raw: Partial<PersistedSiteStylesHistory> | undefined,
): PersistedSiteStylesHistory => {
  const past = Array.isArray(raw?.historyPast)
    ? raw.historyPast
        .filter((s): s is SiteStyles => Boolean(s) && typeof s === 'object')
        .map(cloneStyles)
        .slice(-SITE_HISTORY_LIMIT)
    : [];
  const future = Array.isArray(raw?.historyFuture)
    ? raw.historyFuture
        .filter((s): s is SiteStyles => Boolean(s) && typeof s === 'object')
        .map(cloneStyles)
        .slice(-SITE_HISTORY_LIMIT)
    : [];
  const alignTargets = (value: unknown, length: number): (string | null)[] => {
    const arr = normalizeTargetArray(value);
    if (arr.length === length) return arr;
    if (arr.length > length) return arr.slice(arr.length - length);
    return [...new Array<null>(length - arr.length).fill(null), ...arr];
  };
  return {
    historyPast: past,
    historyFuture: future,
    historyPastTargets: alignTargets(raw?.historyPastTargets, past.length),
    historyFutureTargets: alignTargets(raw?.historyFutureTargets, future.length),
  };
};

export const readPersistedEditorHistory = (accountId: string): CombinedEditorHistoryBlob => {
  const storage = getStorage();
  if (!storage) return emptyCombinedEditorHistory();

  const key = getStorageKey(accountId);
  try {
    const raw = storage.getItem(key);
    if (!raw) return emptyCombinedEditorHistory();
    const parsed = JSON.parse(raw) as Partial<CombinedEditorHistoryBlob>;

    const site = normalizeSiteSide(parsed.site);
    const past = normalizePageSide(parsed.page, site.historyPast.length);
    const future = normalizePageSideFuture(parsed.page, site.historyFuture.length);

    return {
      page: {
        historyPast: past.pageSnapshots,
        historyFuture: future.pageSnapshots,
        historyPastTargets: past.pageTargets,
        historyFutureTargets: future.pageTargets,
        historyOrder: past.order,
        historyOrderFuture: future.order,
      },
      site,
    };
  } catch {
    storage.removeItem(key);
    return emptyCombinedEditorHistory();
  }
};

export const purgeOtherEditorHistoryAccounts = (currentAccountId: string): void => {
  const storage = getStorage();
  if (!storage) return;
  const currentKey = getStorageKey(currentAccountId);
  const stale: string[] = [];
  for (let i = 0; i < storage.length; i += 1) {
    const key = storage.key(i);
    if (key?.startsWith(`${STORAGE_KEY_PREFIX}:`) && key !== currentKey) {
      stale.push(key);
    }
  }
  stale.forEach((key) => {
    storage.removeItem(key);
  });
};

let pageApplier: ((page: PersistedPageEditorHistory) => void) | null = null;
let siteApplier: ((site: PersistedSiteStylesHistory) => void) | null = null;
let crossTabAttached = false;

const ensureCrossTabListener = (): void => {
  if (crossTabAttached) return;
  const win = globalThis.window;
  if (!win) return;
  crossTabAttached = true;
  win.addEventListener('storage', (event) => {
    const accountId = accountIdProvider?.();
    if (!accountId) return;
    if (event.key !== getStorageKey(accountId)) return;

    const blob = readPersistedEditorHistory(accountId);
    withApplyingPersistedEditorHistory(() => {
      pageApplier?.(blob.page);
      siteApplier?.(blob.site);
    });
  });
};

export const registerPageHistoryApplier = (
  fn: (page: PersistedPageEditorHistory) => void,
): void => {
  pageApplier = fn;
  ensureCrossTabListener();
};

export const registerSiteHistoryApplier = (
  fn: (site: PersistedSiteStylesHistory) => void,
): void => {
  siteApplier = fn;
  ensureCrossTabListener();
};
